import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * ContactForm — body of the public /contact page.
 *
 * Walter 2026-05-10: invokes the sendContactMessage Cloud Function via a
 * Firebase Hosting rewrite (/api/sendContactMessage), mirroring the same
 * pattern useAuth uses for sendMagicLinkEmail. Same-origin POST avoids
 * CORS and works for unauthenticated visitors (the function is
 * intentionally invoker:"public" — validation + rate-limit live
 * server-side). httpsCallable would have worked too but the raw POST
 * keeps zero auth-SDK coupling on a page anyone can land on.
 */
const ENDPOINT = '/api/sendContactMessage';

const SUBJECTS = [
  'Question',
  'Bug report',
  'Feature request',
  'Partnership / press',
  'Something else',
];

// Walter 2026-05-10: mirrors the email regex used in sendMagicLinkEmail
// + Firebase Auth — same shape, lenient enough not to bounce valid
// addresses, strict enough to catch typos like "foo@bar".
const EMAIL_RE = /\S+@\S+\.\S+/;

const MIN_MESSAGE = 10;
const MAX_MESSAGE = 2000;

export default function ContactForm({ user }) {
  const isAuthed = !!user && !user.isAnonymous;
  const [name, setName] = useState(isAuthed ? (user.displayName || '') : '');
  const [email, setEmail] = useState(isAuthed ? (user.email || '') : '');
  const [subject, setSubject] = useState(SUBJECTS[0]);
  const [message, setMessage] = useState('');
  // Honeypot field. Real users don't fill this (it's hidden via CSS +
  // aria-hidden + tabIndex=-1 + autocomplete=off). Bots that walk the
  // DOM to populate every input will. Server rejects when populated.
  const [website, setWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const successRef = useRef(null);

  // If auth state arrives after mount (the page renders before the
  // anon-vs-real determination completes), backfill the prefill fields
  // — but only if the user hasn't started typing into them yet.
  useEffect(() => {
    if (!isAuthed) return;
    setName((cur) => cur || user.displayName || '');
    setEmail((cur) => cur || user.email || '');
  }, [isAuthed, user]);

  // Move focus to the success message after a successful submit so a
  // keyboard / screen-reader user lands somewhere sensible (not back at
  // the now-removed Send button).
  useEffect(() => {
    if (submitted && successRef.current) successRef.current.focus();
  }, [submitted]);

  function validate() {
    const errs = {};
    if (!name.trim()) errs.name = 'Please enter your name.';
    if (!email.trim()) errs.email = 'Please enter your email.';
    else if (!EMAIL_RE.test(email.trim())) errs.email = 'That email looks off — double-check it?';
    if (!message.trim()) errs.message = 'Please write a message.';
    else if (message.trim().length < MIN_MESSAGE) errs.message = `A bit more, please (at least ${MIN_MESSAGE} characters).`;
    else if (message.length > MAX_MESSAGE) errs.message = `That's over the ${MAX_MESSAGE}-character limit.`;
    return errs;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // sendMagicLinkEmail uses the onCall envelope ({ data: ... })
        // even though it's posted via a hosting rewrite — the v2
        // onCall handler reads request.data either way. Same here.
        body: JSON.stringify({
          data: {
            name: name.trim(),
            email: email.trim(),
            subject,
            message: message.trim(),
            website, // honeypot — should always be empty
          },
        }),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        let parsed = null;
        try { parsed = JSON.parse(body); } catch {}
        const inner = parsed?.error?.message || '';
        // Map known server discriminators to user-friendly copy.
        if (inner === 'rate-limited') {
          throw new Error("You've sent a few messages already — please wait a bit before sending another.");
        }
        if (inner === 'invalid-input') {
          throw new Error('Some of the fields look off. Please double-check and try again.');
        }
        // Honeypot rejection comes back the same way; we don't show
        // a special message because real users will never see it.
        throw new Error("Couldn't send your message right now. Please try again in a moment.");
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Couldn't send your message.");
    } finally {
      setSubmitting(false);
    }
  }

  function handleSendAnother() {
    setSubmitted(false);
    setError(null);
    setFieldErrors({});
    setMessage('');
    setSubject(SUBJECTS[0]);
    // Keep name + email — likely the same person still.
  }

  if (submitted) {
    return (
      <div
        ref={successRef}
        tabIndex={-1}
        style={{
          maxWidth: 480,
          margin: '0 auto',
          padding: '32px 28px',
          background: 'var(--surface2)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          textAlign: 'center',
          outline: 'none',
        }}
      >
        <div style={{
          fontFamily: 'var(--mono)',
          fontSize: '18px',
          fontWeight: 600,
          color: 'var(--accent)',
          marginBottom: '12px',
        }}>
          Thanks — got it.
        </div>
        <div style={{
          fontFamily: 'var(--sans)',
          fontSize: '15px',
          color: 'var(--text)',
          lineHeight: 1.55,
          marginBottom: '20px',
        }}>
          We read every message. If you asked a question, expect a reply within a day or two — usually from Tom himself.
        </div>
        <button
          type="button"
          onClick={handleSendAnother}
          style={{
            background: 'transparent',
            color: 'var(--accent)',
            border: '1px solid var(--accent)',
            padding: '8px 18px',
            borderRadius: 'var(--radius)',
            fontFamily: 'var(--mono)',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          send another
        </button>
        <div style={{ marginTop: 16, fontFamily: 'var(--mono)', fontSize: '12px' }}>
          <Link to="/landing" style={{ color: 'var(--text-dim)', textDecoration: 'none' }}>
            ← back home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      style={{
        maxWidth: 480,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '18px',
      }}
    >
      <Field
        label="Your name"
        htmlFor="contact-name"
        error={fieldErrors.name}
      >
        <input
          id="contact-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="name"
          required
          aria-invalid={!!fieldErrors.name}
          aria-describedby={fieldErrors.name ? 'contact-name-err' : undefined}
          style={inputStyle(!!fieldErrors.name)}
        />
      </Field>

      <Field
        label="Your email"
        htmlFor="contact-email"
        error={fieldErrors.email}
      >
        <input
          id="contact-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
          aria-invalid={!!fieldErrors.email}
          aria-describedby={fieldErrors.email ? 'contact-email-err' : undefined}
          style={inputStyle(!!fieldErrors.email)}
        />
      </Field>

      <Field
        label="Subject"
        htmlFor="contact-subject"
      >
        <select
          id="contact-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          required
          style={{ ...inputStyle(false), appearance: 'none', cursor: 'pointer' }}
        >
          {SUBJECTS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </Field>

      <Field
        label="Message"
        htmlFor="contact-message"
        error={fieldErrors.message}
        hint={`${message.length} / ${MAX_MESSAGE}`}
      >
        <textarea
          id="contact-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={7}
          required
          minLength={MIN_MESSAGE}
          maxLength={MAX_MESSAGE}
          aria-invalid={!!fieldErrors.message}
          aria-describedby={fieldErrors.message ? 'contact-message-err' : undefined}
          style={{
            ...inputStyle(!!fieldErrors.message),
            resize: 'vertical',
            minHeight: '140px',
            lineHeight: 1.55,
          }}
        />
      </Field>

      {/* Honeypot. Hidden from users via inline styles + aria-hidden +
          tabIndex=-1; autocomplete=off so password managers don't fill
          it. A bot that auto-fills every input will populate this and
          the server will reject the submission. Walter 2026-05-10: kept
          the field NAME literal "website" because that's what naive
          form-spammers most commonly target. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: '-10000px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      >
        <label htmlFor="contact-website">Website (leave empty)</label>
        <input
          id="contact-website"
          type="text"
          name="website"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      {error && (
        <div
          role="alert"
          style={{
            padding: '10px 12px',
            background: 'rgba(224, 112, 80, 0.10)',
            border: '1px solid var(--danger)',
            borderRadius: 'var(--radius)',
            color: 'var(--danger)',
            fontFamily: 'var(--mono)',
            fontSize: '13px',
          }}
        >
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        style={{
          background: submitting ? 'var(--surface2)' : 'var(--accent)',
          color: submitting ? 'var(--text-dim)' : 'var(--bg)',
          border: 'none',
          padding: '12px 22px',
          borderRadius: 'var(--radius)',
          fontFamily: 'var(--mono)',
          fontSize: '14px',
          fontWeight: 600,
          cursor: submitting ? 'default' : 'pointer',
          letterSpacing: '0.3px',
          alignSelf: 'flex-start',
        }}
      >
        {submitting ? 'sending…' : 'send message'}
      </button>
    </form>
  );
}

function Field({ label, htmlFor, error, hint, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: '12px',
      }}>
        <label
          htmlFor={htmlFor}
          style={{
            fontFamily: 'var(--mono)',
            fontSize: '12px',
            color: 'var(--text-dim)',
            letterSpacing: '0.3px',
          }}
        >
          {label}
        </label>
        {hint && (
          <span style={{
            fontFamily: 'var(--mono)',
            fontSize: '11px',
            color: 'var(--text-dimmer)',
          }}>
            {hint}
          </span>
        )}
      </div>
      {children}
      {error && (
        <div
          id={`${htmlFor}-err`}
          style={{
            fontFamily: 'var(--mono)',
            fontSize: '12px',
            color: 'var(--danger)',
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

function inputStyle(hasError) {
  return {
    background: 'var(--surface)',
    color: 'var(--text)',
    border: `1px solid ${hasError ? 'var(--danger)' : 'var(--border)'}`,
    borderRadius: 'var(--radius)',
    padding: '10px 12px',
    // 16px+ kills iOS Safari's auto-zoom on focus. Cheap & critical
    // on mobile — a zoom-then-unzoom cycle is jarring on a form.
    fontSize: '16px',
    fontFamily: 'var(--mono)',
    width: '100%',
    boxSizing: 'border-box',
    outline: 'none',
  };
}
